/**
 * CMS API client — the editable marketing-site content (banners, text sections, hero, cards).
 * Public reads render the site; admin CRUD/reorder carry the authenticated headers (server-
 * gated to admins). Each block is a { type, config } where config holds the type's fields.
 */

import { authHeaders } from "./save";

export type BlockType = "banner" | "text" | "hero" | "cards" | "spacer" | "embed" | "products";

/** A card entry inside a "cards" block. */
export interface CardItem { icon?: string; title?: string; sub?: string; link?: string; }

/** Per-type config (all optional; renderers use sensible defaults). Extra keys are ignored,
 *  so adding a field later needs no migration. */
export interface BlockConfig {
  // shared layout
  maxWidth?: number;         // px cap on the block's content width
  align?: "left" | "center" | "right";
  bg?: string;               // background colour (CSS)
  padY?: number;             // vertical padding (px) around the block
  // banner / image
  imageUrl?: string;
  alt?: string;
  height?: number;           // banner height in px
  width?: number;            // banner width in px (0/undefined = full)
  link?: string;             // click-through URL
  radius?: number;           // corner radius px
  // a landing-banner SLIDE (an image block on page "landing-banner"): optional overlay button + a show window
  startsAt?: string;         // YYYY-MM-DD — hidden before this day
  endsAt?: string;           // YYYY-MM-DD — hidden after this day (inclusive)
  // text / hero
  heading?: string;
  body?: string;
  // hero
  eyebrow?: string;
  buttonText?: string;
  buttonLink?: string;
  // cards
  cards?: CardItem[];
  // embed (custom HTML/CSS/JS — rendered in a SANDBOXED iframe, isolated from the site)
  html?: string;             // custom HTML body
  css?: string;              // custom CSS (injected into the iframe <head>)
  js?: string;               // custom JS (runs inside the sandbox only)
  // products (product-highlight): which products to show + presentation
  productIds?: number[];     // specific product ids; empty/undefined = all active products
  showPrice?: boolean;       // show the price + Buy control (default true)
  columns?: number;          // grid columns (default 3)
}

export interface Block {
  id: number;
  page: string;
  type: BlockType;
  position: number;
  config: BlockConfig;
  active: boolean;
}

/** Public: the active blocks for a page (for rendering). Returns [] if the page has none. */
export async function getPageBlocks(page: string): Promise<Block[]> {
  try {
    const res = await fetch(`/api/pages/${page}/blocks`);
    if (!res.ok) return [];
    return (await res.json()).blocks ?? [];
  } catch {
    return [];   // never break the page if the CMS is unavailable
  }
}

// ---- admin ----------------------------------------------------------------

export async function adminGetBlocks(page: string): Promise<{ blocks: Block[]; pages: string[] }> {
  const res = await fetch(`/api/admin/pages/${page}/blocks`, { headers: await authHeaders() });
  if (!res.ok) throw new Error(`blocks ${res.status}`);
  return res.json();
}

export async function adminCreateBlock(page: string, type: BlockType, config: BlockConfig): Promise<Block> {
  const res = await fetch(`/api/admin/pages/${page}/blocks`, {
    method: "POST",
    headers: await authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({ type, config }),
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.error || `create ${res.status}`);
  return d.block;
}

export async function adminUpdateBlock(page: string, id: number, type: BlockType, config: BlockConfig, active: boolean): Promise<Block> {
  const res = await fetch(`/api/admin/pages/${page}/blocks/${id}`, {
    method: "PUT",
    headers: await authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({ type, config, active }),
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.error || `update ${res.status}`);
  return d.block;
}

export async function adminDeleteBlock(page: string, id: number): Promise<void> {
  const res = await fetch(`/api/admin/pages/${page}/blocks/${id}`, { method: "DELETE", headers: await authHeaders() });
  if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || `delete ${res.status}`); }
}

export async function adminReorderBlocks(page: string, order: number[]): Promise<void> {
  const res = await fetch(`/api/admin/pages/${page}/reorder`, {
    method: "POST",
    headers: await authHeaders({ "content-type": "application/json" }),
    body: JSON.stringify({ order }),
  });
  if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || `reorder ${res.status}`); }
}
